import apiClient from '../apiClient';
import { Route } from '../routes/route.vm';
import { Stage } from '../stages/stage.vm';
import { ContentInteractionEntityApi } from './content.api-model';
import { mapContentCollectionFromApiToVm, mapContentFromApiToVm } from './content.mapper';
import { Content } from './content.vm';

const CONTENT_BASE_URL = '/contents';
const INTERACTION_BASE_URL = '/user-content-interaction';

export const contentsService = {
  getContentbyId: async (contentId: Content['id']) => {
    if (!contentId) {
      throw new Error('Content Id not provided');
    }
    try {
      const response = await apiClient.get(`${CONTENT_BASE_URL}/${contentId}`);
      return mapContentFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to get content');
    }
  },

  getContentsByRoute: async (routeId: Route['id']) => {
    if (!routeId) {
      throw new Error('Route Id not provided');
    }
    try {
      const response = await apiClient.get(`${CONTENT_BASE_URL}`, {
        params: { routeId },
      });
      return mapContentCollectionFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to get contents by route');
    }
  },

  getContentsByStage: async (stageId: Stage['id']) => {
    if (!stageId) {
      throw new Error('Stage Id not provided');
    }
    try {
      const response = await apiClient.get(`${CONTENT_BASE_URL}`, {
        params: { stageId },
      });
      return mapContentCollectionFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to get contents by stage');
    }
  },

  getAllContents: async () => {
    const response = await apiClient.get(`${CONTENT_BASE_URL}`);
    return mapContentCollectionFromApiToVm(response.data);
  },

  createContent: async (content: FormData) => {
    if (!content) {
      throw new Error('Content not provided');
    }
    try {
      const response = await apiClient.post(`${CONTENT_BASE_URL}`, content, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return mapContentFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to create content');
    }
  },

  updateContent: async (contentId: string, content: FormData) => {
    if (!contentId) {
      throw new Error('Content Id not provided');
    }
    if (!content) {
      throw new Error('Content data not provided');
    }
    try {
      const response = await apiClient.patch(`${CONTENT_BASE_URL}/${contentId}`, content, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return mapContentFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to update content');
    }
  },

  deleteContent: async (contentId: string) => {
    if (!contentId) {
      throw new Error('Content Id not provided');
    }
    try {
      const response = await apiClient.delete(`${CONTENT_BASE_URL}/${contentId}`);
      return response.data;
    } catch (e) {
      throw new Error('Failed to delete content');
    }
  },

  reorder: async (newContentsOrdering: Array<{ contentId: Content['id']; newOrder: number }>) => {
    if (!newContentsOrdering) {
      throw new Error('New contents ordering not provided');
    }
    try {
      const response = await apiClient.patch(`${CONTENT_BASE_URL}/reorder`, {
        contents: newContentsOrdering,
      });
      return response.data;
    } catch (e) {
      throw new Error('Failed to reorder');
    }
  },

  // CONTENT INTERACTIONS

  createContentInteraction: async ({
    contentId,
    userRouteProgressId,
  }: {
    contentId: Content['id'];
    userRouteProgressId: Route['userRouteProgressId'];
  }) => {
    if (!contentId) {
      throw new Error('Content Id not provided');
    }

    if (!userRouteProgressId) {
      throw new Error('User route progress Id not provided');
    }
    try {
      const response = await apiClient.post(`${INTERACTION_BASE_URL}`, {
        contentId,
        userRouteProgressId,
      });
      return response.data;
    } catch (e) {
      throw new Error('Failed to create content interaction');
    }
  },

  updateContentInteraction: async (
    contentInteractionId?: string,
    contentInteractionData?: Partial<ContentInteractionEntityApi>,
  ) => {
    if (!contentInteractionId) {
      throw new Error('Content interaction id not provided');
    }
    try {
      const response = await apiClient.patch(
        `${INTERACTION_BASE_URL}/${contentInteractionId}`,
        contentInteractionData,
      );
      return response.data;
    } catch (e) {
      throw new Error('Failed to update content interaction');
    }
  },

  completeContent: async ({
    contentId,
    contentInteractionId,
    userRouteProgressId,
  }: {
    contentId: Content['id'];
    contentInteractionId?: Content['contentInteractionId'];
    userRouteProgressId: Route['userRouteProgressId'];
  }) => {
    if (!contentId) {
      throw new Error('Content Id not provided');
    }

    if (!userRouteProgressId) {
      throw new Error('User route progress Id not provided');
    }
    try {
      const response = await apiClient.patch(`${INTERACTION_BASE_URL}/${contentInteractionId}/complete`, {
        contentId,
        userRouteProgressId,
        completed: true,
      });
      return response.data;
    } catch (e) {
      throw new Error('Failed to create content interaction');
    }
  },

  generateAnalysis: async (contentInteractionId: string) => {
    if (!contentInteractionId) {
      throw new Error('Content interaction id not provided');
    }
    try {
      const response = await apiClient.patch(`${INTERACTION_BASE_URL}/${contentInteractionId}/generate-analysis`);
      return response.data;
    } catch (e) {
      throw new Error('Failed to generate analysis');
    }
  },
};
